// The place block on the event and series forms: street, city, state, ZIP and two
// phone numbers, under the existing Location (venue name) box.
//
// All six are optional (Will, 2026-10-17). Shared by both forms because they were
// about to be copied into each, which is how the two drift. A business lookup that
// fills these in from a search is possible but deliberately not built: it sends
// what the member types to a third party and needs a privacy-policy line.

import { el } from '../dom.js';

export interface PlaceValues {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  locationPhone?: string;
  organizerPhone?: string;
}

export interface PlaceFields {
  element: HTMLElement;
  /** Trimmed values, '' for a box left empty. */
  value(): Required<PlaceValues>;
}

function box(
  idPrefix: string,
  key: string,
  label: string,
  max: number,
  initial: string,
  extra: Record<string, string> = {},
): { label: HTMLElement; input: HTMLInputElement } {
  const id = `${idPrefix}-${key}`;
  const input = el('input', {
    id,
    type: 'text',
    maxlength: String(max),
    value: initial,
    autocomplete: 'off',
    ...extra,
  }) as HTMLInputElement;
  return { label: el('label', { for: id }, label), input };
}

export function placeFields(idPrefix: string, existing?: PlaceValues | null): PlaceFields {
  const address = box(idPrefix, 'address', 'Street address', 191, existing?.address ?? '', {
    autocomplete: 'street-address',
  });
  const city = box(idPrefix, 'city', 'City', 100, existing?.city ?? '');
  const state = box(idPrefix, 'state', 'State', 50, existing?.state ?? '');
  const zip = box(idPrefix, 'zip', 'ZIP', 20, existing?.zip ?? '', { inputmode: 'numeric' });
  const locPhone = box(idPrefix, 'loc-phone', 'Location phone', 32, existing?.locationPhone ?? '', {
    type: 'tel',
    inputmode: 'tel',
  });
  const orgPhone = box(idPrefix, 'org-phone', 'Organizer phone', 32, existing?.organizerPhone ?? '', {
    type: 'tel',
    inputmode: 'tel',
  });

  const element = el('div', { class: 'place-fields' }, [
    address.label,
    address.input,
    el('div', { class: 'place-row-3' }, [
      el('div', {}, [city.label, city.input]),
      el('div', {}, [state.label, state.input]),
      el('div', {}, [zip.label, zip.input]),
    ]),
    locPhone.label,
    locPhone.input,
    orgPhone.label,
    orgPhone.input,
    el('p', { class: 'muted small' }, 'All of these are optional.'),
  ]);

  return {
    element,
    value: () => ({
      address: address.input.value.trim(),
      city: city.input.value.trim(),
      state: state.input.value.trim(),
      zip: zip.input.value.trim(),
      locationPhone: locPhone.input.value.trim(),
      organizerPhone: orgPhone.input.value.trim(),
    }),
  };
}

/** "123 Main St, Denver, CO 80202", whatever subset exists. */
export function addressLine(p: PlaceValues): string {
  const cityState = [p.city, [p.state, p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [p.address, cityState].filter(Boolean).join(', ');
}

/**
 * Where-block rows for the detail pages: the address with a Directions link, then
 * a tap-to-call link per phone. The maps link is a plain https link the member
 * chooses to follow; nothing is sent from this app when it renders.
 */
export function placeRows(
  venue: string,
  p: PlaceValues,
  row: (label: string, value: string | (string | HTMLElement)[]) => HTMLElement,
): HTMLElement[] {
  const out: HTMLElement[] = [];
  const line = addressLine(p);
  if (venue) out.push(row('Where', venue));
  if (line) {
    const query = encodeURIComponent([venue, line].filter(Boolean).join(', '));
    out.push(
      row('Address', [
        line,
        ' ',
        el(
          'a',
          { href: `https://www.google.com/maps/search/?api=1&query=${query}`, target: '_blank', rel: 'noopener' },
          'Directions',
        ),
      ]),
    );
  }
  const tel = (n: string): HTMLElement => el('a', { href: `tel:${n.replace(/[^0-9+]/g, '')}` }, n);
  if (p.locationPhone) out.push(row('Location phone', [tel(p.locationPhone)]));
  if (p.organizerPhone) out.push(row('Organizer phone', [tel(p.organizerPhone)]));
  return out;
}
